import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BackgroundJobs, SpecraBadge } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { BookOpen, ChevronRight, FolderOpen, MessageSquare, Plus, Sparkles, Upload, Users } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useChatHistory } from '../../components/ChatHistory';
import { ErrorState, PageBody, PageHero } from '../../components/layout';
import { useAuth } from '../../contexts/authContext';
import { useProjects } from '../../hooks/useProjects';
import { selectProcessingJobs, useProcessingJobsStore } from '../../lib/processing/jobsStore';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { getProjectPath } from '../../lib/utils/project-routes';
import { compareByDateDesc, formatRelativeTime, greeting, staleness } from '../../utils/time';
import { DeliverySummaryCard } from './DeliverySummaryCard';
import { MyAssignedActionsCard } from './MyAssignedActionsCard';
const typeLabel = {
    proposal: 'Proposal',
    contract: 'Contract',
    pre_award: 'Pre-Award',
    general: 'General'
};
const typeBadgeVariant = {
    proposal: 'info',
    contract: 'success',
    pre_award: 'warning',
    general: 'neutral'
};
// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function QuickActionCompact({ icon, label, href }) {
    return /*#__PURE__*/ _jsxs(Link, {
        to: href,
        className: "flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-foreground transition-colors hover:border-brand hover:bg-brand-muted/40",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground [&_svg]:size-3.5",
                children: icon
            }),
            label
        ]
    });
}
function ProjectRow({ project, onClick, isLast }) {
    const activity = staleness(project.lastActivityAt);
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        onClick: onClick,
        className: `flex w-full items-center gap-3.5 px-[18px] py-3.5 text-left transition-colors hover:bg-muted/50 cursor-pointer ${isLast ? '' : 'border-b border-border'}`,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "flex size-8 shrink-0 items-center justify-center rounded-[7px] bg-muted text-muted-foreground",
                children: /*#__PURE__*/ _jsx(FolderOpen, {
                    className: "size-3.5"
                })
            }),
            /*#__PURE__*/ _jsxs("span", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate text-[13.5px] font-medium text-foreground",
                                children: project.name
                            }),
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: typeBadgeVariant[project.type] ?? 'neutral',
                                children: typeLabel[project.type] ?? project.type
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "mt-0.5 flex items-center gap-2 text-[11.5px] text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: activity.label
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: "·"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex items-center gap-1",
                                children: [
                                    /*#__PURE__*/ _jsx(Users, {
                                        className: "size-3"
                                    }),
                                    project.memberCount
                                ]
                            })
                        ]
                    })
                ]
            }),
            activity.stale && /*#__PURE__*/ _jsx("span", {
                className: "shrink-0 text-[11.5px] font-medium text-warning",
                children: "Needs attention"
            }),
            /*#__PURE__*/ _jsx(ChevronRight, {
                className: "size-3 shrink-0 text-muted-foreground/50",
                "aria-hidden": "true"
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
export function DashboardRedesign() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const setShowBreadcrumbs = useLayoutStore((s)=>s.setShowBreadcrumbs);
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        setShowHeader(false);
        return ()=>{
            setShowBreadcrumbs(true);
            setShowHeader(true);
        };
    }, [
        setShowBreadcrumbs,
        setShowHeader
    ]);
    // Data
    const { data: projectsData, isLoading: projectsLoading, error: projectsError, refetch: refetchProjects } = useProjects({
        status: 'active'
    });
    const projects = projectsData?.projects ?? [];
    const recentProjects = projects.slice(0, 6);
    const activeJobs = useProcessingJobsStore(selectProcessingJobs);
    const mappedJobs = activeJobs.map((j)=>({
            id: j.batchId,
            name: j.label,
            target: j.kind,
            pct: j.percent
        }));
    const { chats, isLoading: chatsLoading, error: chatsError, refetch: refetchChats } = useChatHistory();
    const lastChat = useMemo(()=>{
        const sorted = [
            ...chats
        ].sort((a, b)=>compareByDateDesc(a.updatedAt, b.updatedAt));
        return sorted[0] ?? null;
    }, [
        chats
    ]);
    // Stats for status line
    const proposalCount = projects.filter((p)=>p.type === 'proposal' || p.type === 'pre_award').length;
    const contractCount = projects.filter((p)=>p.type === 'contract').length;
    return /*#__PURE__*/ _jsxs(PageBody, {
        children: [
            /*#__PURE__*/ _jsx(PageHero, {
                title: greeting(user?.name),
                subtitle: projects.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        proposalCount > 0 && /*#__PURE__*/ _jsxs("span", {
                            children: [
                                proposalCount,
                                " active proposal",
                                proposalCount !== 1 ? 's' : ''
                            ]
                        }),
                        proposalCount > 0 && contractCount > 0 && /*#__PURE__*/ _jsx("span", {
                            children: " · "
                        }),
                        contractCount > 0 && /*#__PURE__*/ _jsxs("span", {
                            children: [
                                contractCount,
                                " contract",
                                contractCount !== 1 ? 's' : ''
                            ]
                        }),
                        proposalCount === 0 && contractCount === 0 && /*#__PURE__*/ _jsxs("span", {
                            children: [
                                projects.length,
                                " active project",
                                projects.length !== 1 ? 's' : ''
                            ]
                        })
                    ]
                }) : 'No active projects yet.',
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "mt-4 flex flex-wrap gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(QuickActionCompact, {
                            icon: /*#__PURE__*/ _jsx(MessageSquare, {}),
                            label: "New Chat",
                            href: "/assistant"
                        }),
                        /*#__PURE__*/ _jsx(QuickActionCompact, {
                            icon: /*#__PURE__*/ _jsx(Plus, {}),
                            label: "New Project",
                            href: "/projects"
                        }),
                        /*#__PURE__*/ _jsx(QuickActionCompact, {
                            icon: /*#__PURE__*/ _jsx(Upload, {}),
                            label: "Upload",
                            href: "/documents/upload"
                        }),
                        /*#__PURE__*/ _jsx(QuickActionCompact, {
                            icon: /*#__PURE__*/ _jsx(BookOpen, {}),
                            label: "Library",
                            href: "/library"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 min-w-0",
                        children: [
                            /*#__PURE__*/ _jsx(DeliverySummaryCard, {
                                companyId: user?.tenant_id
                            }),
                            /*#__PURE__*/ _jsx(MyAssignedActionsCard, {}),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mb-3.5 flex items-baseline justify-between",
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-sm font-medium text-foreground",
                                        children: "Active Projects"
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: "/projects",
                                        className: "text-xs text-brand hover:underline",
                                        children: "View all →"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "rounded-lg border border-border bg-card overflow-hidden",
                                children: projectsLoading ? /*#__PURE__*/ _jsx("div", {
                                    className: "p-3 space-y-1",
                                    children: [
                                        1,
                                        2,
                                        3,
                                        4
                                    ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                            className: "h-12 rounded-md bg-muted/50 animate-pulse"
                                        }, i))
                                }) : projectsError ? /*#__PURE__*/ _jsx(ErrorState, {
                                    message: "Failed to load projects. Please try again.",
                                    onRetry: refetchProjects
                                }) : recentProjects.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                                    className: "text-center py-16",
                                    children: [
                                        /*#__PURE__*/ _jsx(FolderOpen, {
                                            className: "h-10 w-10 mx-auto mb-3 text-muted-foreground"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground mb-4",
                                            children: "No projects yet"
                                        }),
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: "/projects",
                                            className: "text-xs text-brand hover:underline",
                                            children: "Create your first project"
                                        })
                                    ]
                                }) : recentProjects.map((p, i)=>/*#__PURE__*/ _jsx(ProjectRow, {
                                        project: p,
                                        onClick: ()=>navigate(getProjectPath(p)),
                                        isLast: i === recentProjects.length - 1
                                    }, p.projectId))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("aside", {
                        className: "hidden w-[324px] flex-shrink-0 flex-col gap-6 self-start rounded-2xl border-border bg-muted/40 p-6 lg:flex",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                        children: "Continue chatting"
                                    }),
                                    chatsLoading ? /*#__PURE__*/ _jsx("div", {
                                        className: "h-10 animate-pulse rounded bg-muted/50"
                                    }) : chatsError ? /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>refetchChats(),
                                        className: "text-xs text-danger hover:underline",
                                        children: "Couldn't load chats — retry"
                                    }) : lastChat ? /*#__PURE__*/ _jsxs(Link, {
                                        to: `/assistant/${lastChat.chatId}`,
                                        className: "group block rounded-lg bg-card px-3.5 py-3",
                                        "data-testid": Testids.dashboard.recentChatItem,
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "text-[13px] font-medium truncate group-hover:text-brand transition-colors",
                                                children: lastChat.title
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "mt-0.5 text-xs text-muted-foreground",
                                                children: formatRelativeTime(lastChat.updatedAt)
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(Link, {
                                        to: "/assistant",
                                        className: "text-xs text-brand hover:underline",
                                        children: "Start a conversation →"
                                    })
                                ]
                            }),
                            mappedJobs.length > 0 && /*#__PURE__*/ _jsx(BackgroundJobs, {
                                jobs: mappedJobs,
                                runningCount: mappedJobs.length
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.dashboard.recentChats,
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mb-2 flex items-baseline justify-between",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                                children: "Recent chats"
                                            }),
                                            /*#__PURE__*/ _jsx(Link, {
                                                to: "/history",
                                                className: "text-xs text-muted-foreground hover:text-foreground",
                                                children: "All →"
                                            })
                                        ]
                                    }),
                                    chatsLoading ? /*#__PURE__*/ _jsx("div", {
                                        className: "space-y-1.5",
                                        children: [
                                            1,
                                            2,
                                            3
                                        ].map((i)=>/*#__PURE__*/ _jsx("div", {
                                                className: "h-8 animate-pulse rounded bg-muted/50"
                                            }, i))
                                    }) : chatsError ? /*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>refetchChats(),
                                        className: "text-xs text-danger hover:underline",
                                        children: "Couldn't load chats — retry"
                                    }) : chats.length === 0 ? /*#__PURE__*/ _jsx("div", {
                                        className: "text-xs text-muted-foreground",
                                        children: "Conversations will appear here."
                                    }) : /*#__PURE__*/ _jsx("div", {
                                        className: "space-y-0.5",
                                        children: [
                                            ...chats
                                        ].sort((a, b)=>compareByDateDesc(a.updatedAt, b.updatedAt)).slice(0, 5).map((chat)=>/*#__PURE__*/ _jsxs(Link, {
                                                to: `/assistant/${chat.chatId}`,
                                                className: "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-card transition-colors",
                                                children: [
                                                    /*#__PURE__*/ _jsx(MessageSquare, {
                                                        className: "size-3 flex-shrink-0 text-muted-foreground"
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "truncate text-foreground/70",
                                                        children: chat.title
                                                    })
                                                ]
                                            }, chat.chatId))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mb-2 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                        children: "Assist"
                                    }),
                                    /*#__PURE__*/ _jsxs(Link, {
                                        className: "flex items-center gap-2.5 rounded-lg border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:text-foreground",
                                        to: "/assistant",
                                        children: [
                                            /*#__PURE__*/ _jsx(Sparkles, {
                                                className: "size-3.5 shrink-0"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "min-w-0 flex-1",
                                                children: "Ask about the portfolio"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
